import { create } from "../../../utils/clienteHttp";

const formatarUrl = url => {
  if (url.indexOf("http:") === 0 || url.indexOf("https:") === 0) return url;
  return window.location.protocol === "https:" ? `https://${url}` : `http://${url}`;
};

// Cliente próprio do votante: não usa o token do síndico (localStorage do painel).
// Mesmas rotas do app das lojas (/unidade/confirmarUnidadev3 e /app/votacoes/*).
const cliente = create({ baseURL: formatarUrl(process.env.REACT_APP_API_URL), timeout: 60000 });

const CHAVE_SESSAO = "va-votar-sessao";
const CHAVE_APP_ID = "va-votar-app-id";
const PREFIXO_RESPONDIDAS = "va-votar-respondidas@";

// A sessão do votante não fica aberta para sempre no navegador:
// expira por inatividade e, de qualquer forma, após um tempo máximo desde a entrada.
export const LIMITE_INATIVIDADE_MS = 30 * 60 * 1000;
export const LIMITE_TOTAL_MS = 4 * 60 * 60 * 1000;

const lerJson = chave => {
  try {
    return JSON.parse(localStorage.getItem(chave));
  } catch (e) {
    return null;
  }
};

export const sessaoExpirada = (sessao, agora = Date.now()) =>
  !sessao ||
  !sessao.iniciadaEm ||
  agora - sessao.iniciadaEm > LIMITE_TOTAL_MS ||
  agora - (sessao.ultimaAtividade || sessao.iniciadaEm) > LIMITE_INATIVIDADE_MS;

// Devolve a sessão gravada (mesmo vencida: quem chama encerra no servidor e limpa os dados).
export const lerSessao = () => lerJson(CHAVE_SESSAO);

export const salvarSessao = sessao => {
  const agora = Date.now();
  const completa = { ...sessao, iniciadaEm: sessao.iniciadaEm || agora, ultimaAtividade: agora };
  try {
    localStorage.setItem(CHAVE_SESSAO, JSON.stringify(completa));
  } catch (e) {
    /* sem armazenamento: a sessão vale só até recarregar */
  }
  return completa;
};

// Marca atividade do votante, renovando o prazo de inatividade.
export const renovarSessao = () => {
  const atual = lerSessao();
  if (!atual || sessaoExpirada(atual)) return atual;
  return salvarSessao(atual);
};

// Remove tudo que o votante deixou no navegador: sessão, votos já respondidos e (opcional) o identificador do aparelho.
export const limparDadosVotante = ({ removerAppId = false } = {}) => {
  try {
    localStorage.removeItem(CHAVE_SESSAO);
    if (removerAppId) localStorage.removeItem(CHAVE_APP_ID);
    Object.keys(localStorage)
      .filter(chave => chave.indexOf(PREFIXO_RESPONDIDAS) === 0)
      .forEach(chave => localStorage.removeItem(chave));
  } catch (e) {
    /* nada a limpar */
  }
};

// Identificador do navegador. A API prende a unidade a um aparelho: o mesmo código só entra em outro aparelho
// depois de sair no primeiro (igual ao app).
export const obterAppId = () => {
  try {
    let id = localStorage.getItem(CHAVE_APP_ID);
    if (!id) {
      const aleatorio =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      id = `web-${aleatorio}`;
      localStorage.setItem(CHAVE_APP_ID, id);
    }
    return id;
  } catch (e) {
    return `web-${Date.now()}`;
  }
};

export const mensagemDeErro = resposta => {
  if (!resposta) return "Erro inesperado. Tente novamente.";
  if (resposta.problem === "NETWORK_ERROR" || resposta.problem === "TIMEOUT_ERROR") return "Problema de conexão. Verifique sua internet.";
  const dados = resposta.data;
  if (typeof dados === "string" && dados) return dados;
  if (dados && dados.errorBackend) return dados.errorBackend;
  if (dados && dados.errors && dados.errors.global) return dados.errors.global;
  return "Não foi possível concluir. Tente novamente em alguns minutos.";
};

const comToken = token => ({ headers: { Authorization: `Bearer ${token}` } });

// Lança a resposta quando não é 2xx; o chamador usa mensagemDeErro.
const exigirOk = resposta => {
  if (!resposta.ok) throw resposta;
  return resposta.data;
};

// Encerra a sessão: libera a unidade na API e apaga os dados do navegador. O identificador do aparelho só é
// removido se a API confirmou o logout; senão a unidade ficaria presa a um aparelho que não existe mais.
export const encerrarSessao = async sessao => {
  const liberada = sessao && sessao.unidade ? await apiVotar.sair(sessao.unidade._id) : true;
  limparDadosVotante({ removerAppId: liberada });
  return liberada;
};

const apiVotar = {
  entrar: hash =>
    cliente.post("/unidade/confirmarUnidadev3", { hash: hash.trim(), appId: obterAppId() }).then(exigirOk),
  // true quando a API confirmou o logout (a unidade fica livre para outro aparelho)
  sair: unidade =>
    cliente
      .post("/unidade/deslogarUnidade", { unidade, appId: obterAppId() })
      .then(r => !!r.ok)
      .catch(() => false),
  abertas: (token, condominioId) =>
    cliente.get(`/app/votacoes/abertasByCondominioId/${condominioId}`, {}, comToken(token)).then(exigirOk),
  encerradas: (token, condominioId) =>
    cliente.get(`/app/votacoes/fechadasByCondominioId/${condominioId}`, {}, comToken(token)).then(exigirOk),
  votacao: (token, id) => cliente.get(`/app/votacoes/findById/${id}`, {}, comToken(token)).then(exigirOk),
  resultado: (token, condominioId, id) =>
    cliente
      .get(`/app/votacoes/resultadoPerguntasByVotacaoId/${condominioId}/${id}`, {}, comToken(token))
      .then(exigirOk),
  validarHora: (token, votacaoId) =>
    cliente.post("/app/votacoes/validarHoraVotacao", { votacaoId }, comToken(token)).then(exigirOk),
  confirmarVoto: (token, dados) =>
    cliente.post("/app/votacoes/confirmarVoto", { ...dados, macAddress: obterAppId(), dispositivo: "Web" }, comToken(token)).then(exigirOk)
};

export default apiVotar;
